import { ShoppingCart, Leaf, User, LogOut, Settings, Heart, Handshake, Search } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '@/context/CartContext';
import { formatPrice } from '@/utils/formatPrice';
import { useAuth } from '@/context/AuthContext';
import { useAdminRole } from '@/hooks/useAdminRole';
import { usePartnerRole } from '@/hooks/usePartnerRole';
import { useCartAnimation } from '@/context/CartAnimationContext';
import { useFavorites } from '@/context/FavoritesContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { NotificationsDropdown } from './NotificationsDropdown';
import { cn } from '@/lib/utils';

const Header = () => {
  const { totalItems, totalPrice } = useCart();
  const { user, profile, signOut } = useAuth();
  const { isAdmin } = useAdminRole();
  const { isPartner } = usePartnerRole();
  const { cartIconRef, isPulsing } = useCartAnimation();
  const { favorites } = useFavorites();
  const navigate = useNavigate();
  const location = useLocation();

  const navLinks = [
    { name: 'Главная', href: '/' },
    { name: 'Каталог', href: '/catalog' },
    { name: 'О нас', href: '/about' },
    { name: 'Контакты', href: '/contacts' },
  ];

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/80 border-b border-border/50 shadow-sm hidden md:block">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex h-16 lg:h-20 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <motion.div
              className="w-9 h-9 lg:w-10 lg:h-10 rounded-xl bg-primary flex items-center justify-center shadow-sm"
              whileHover={{ scale: 1.05, rotate: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <Leaf className="w-5 h-5 lg:w-6 lg:h-6 text-primary-foreground" />
            </motion.div>
            <span className="font-heading text-xl lg:text-2xl font-bold text-foreground tracking-tight">
              Фермер<span className="text-primary">Чел</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1 lg:gap-2">
            {navLinks.map((link) => {
              const active = isActive(link.href);
              return (
                <Link
                  key={link.name}
                  to={link.href}
                  className={cn(
                    "relative px-4 py-2 rounded-full text-sm font-medium transition-all font-body",
                    active
                      ? "bg-foreground text-background"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  )}
                >
                  {link.name}
                  {active && (
                    <motion.span
                      layoutId="navActive"
                      className="absolute inset-0 rounded-full bg-foreground -z-10"
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-1 lg:gap-2">
            {/* Search */}
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full hover:bg-secondary"
              onClick={() => navigate('/catalog')}
              aria-label="Поиск"
            >
              <Search className="h-5 w-5" />
            </Button>

            {/* Notifications */}
            {user && <NotificationsDropdown />}

            {/* Auth / User */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="rounded-full hover:bg-secondary gap-2 px-2"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={profile?.avatar_url || undefined} />
                      <AvatarFallback className="bg-primary/20 text-primary text-xs">
                        {profile?.first_name?.[0] || user.email?.[0]?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden lg:inline text-sm font-medium font-body">
                      {profile?.first_name || 'Профиль'}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-card border-border">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{profile?.first_name} {profile?.last_name}</p>
                    {profile?.username && (
                      <p className="text-xs text-muted-foreground">@{profile.username}</p>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                    <User className="mr-2 h-4 w-4" />
                    Мой профиль
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer">
                      <Settings className="mr-2 h-4 w-4" />
                      Админ-панель
                    </DropdownMenuItem>
                  )}
                  {isPartner && (
                    <DropdownMenuItem onClick={() => navigate('/partner')} className="cursor-pointer">
                      <Handshake className="mr-2 h-4 w-4" />
                      Панель партнёра
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="text-destructive cursor-pointer">
                    <LogOut className="mr-2 h-4 w-4" />
                    Выйти
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/auth')}
                className="rounded-full hover:bg-secondary gap-2"
              >
                <User className="h-4 w-4" />
                <span className="hidden lg:inline font-body">Войти</span>
              </Button>
            )}

            {/* Favorites */}
            <Link to="/favorites">
              <motion.div whileTap={{ scale: 0.95 }}>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative rounded-full hover:bg-secondary"
                >
                  <Heart className="h-5 w-5" />
                  <AnimatePresence>
                    {favorites.length > 0 && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        className="absolute -top-1 -right-1"
                      >
                        <Badge
                          className="h-5 w-5 flex items-center justify-center p-0 text-xs bg-primary text-primary-foreground border-0"
                        >
                          {favorites.length}
                        </Badge>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Button>
              </motion.div>
            </Link>

            {/* Cart */}
            <Link to="/cart">
              <motion.div whileTap={{ scale: 0.95 }} className="flex items-center">
                <Button
                  ref={cartIconRef}
                  variant={totalItems > 0 ? 'default' : 'ghost'}
                  size="sm"
                  className={cn(
                    "relative rounded-full gap-2",
                    totalItems > 0
                      ? "bg-primary text-primary-foreground hover:bg-primary/90 pl-3 pr-4"
                      : "hover:bg-secondary px-2"
                  )}
                  data-cart-icon
                >
                  <span className="relative">
                    <ShoppingCart className="h-5 w-5" />
                    <AnimatePresence>
                      {totalItems > 0 && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: isPulsing ? [1, 1.25, 1] : 1 }}
                          exit={{ scale: 0 }}
                          transition={{ duration: 0.3 }}
                          className="absolute -top-2 -right-2"
                        >
                          <Badge
                            className="h-5 w-5 flex items-center justify-center p-0 text-xs bg-primary-foreground text-primary border-0"
                          >
                            {totalItems}
                          </Badge>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </span>
                  {totalPrice > 0 && (
                    <span className="hidden sm:inline font-semibold font-body">
                      {formatPrice(totalPrice)}
                    </span>
                  )}
                </Button>
              </motion.div>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
